import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { distinctUntilChanged, from, map, takeUntil } from 'rxjs';
import { ComponentWithDestroy } from 'src/app/modules/shared/component-with-destroy';
import isEqual from 'lodash/isEqual';
import * as firestore from 'firebase/firestore';
import { HLConversationModel, HLConversationStatus, IConversationClickEvent, IFirebaseFilterCriteria, HLConversationAgent } from 'src/app/models/conversations';
import { AuthService } from 'src/app/services/auth.service';
import { HumanInLoopService } from 'src/app/services/firestore/human-in-loop.service';
import { ApiGatewayAnalyticsService } from 'src/app/services/api-gateway-analytics/api-gateway-analytics.service';
import { TimeInterval } from '../../analytics/_types/TimeInterval';
import { BotModel } from 'src/app/models/bot';
import { Intent } from 'src/app/services/api-gateway-analytics/types';
import { StorageService, StorageServiceFactory } from 'src/app/modules/storage';

interface IntentFilterOption {
  label: string;
  value: string;
}

interface IntentFilterGroupOption {
  label: string;
  values: string[];
}

const ASSISTANT_STOPPED_INTENT: IntentFilterOption = { label: 'Assistant Stopped', value: '' };

const DEFAULT_SELECTED_INTENT_CODES: string[] = [
  '',
  'differentDealer',
  'alreadyScheduled',
  'outOfTown',
  'mileage',
  'notDueYet',
  'followUp',
  'scheduleServiceExisting',
  'scheduleConfirmed',
  'rescheduleConfirmed',
  'confirmedAppointmentTime',
  'contactMe',
  'userDissatisfied',
  'userGrateful',
  'needsReview',
  'loaner',
  'csiNegative',
  'csiPositive',
  'costEstimate',
  'whyService',
  'timeEstimate',
  'scheduleService',
  'problemWithVehicle',
  'scheduleServiceAutomated',
  'scheduleServiceAlternate',
  'rescheduleService',
  'rescheduleServiceAutomated',
  'rescheduleServiceAlternate',
  'scheduleServiceAutomatedFailed',
  'rescheduleServiceAutomatedFailed',
  'vehicleAppraisal',
  'appointmentCancellation',
  'appointmentCancellationAutomated',
  'appointmentCancellationAutomatedFailed',
];

const AVAILABLE_INTENT_CODES: string[] = [
  ...DEFAULT_SELECTED_INTENT_CODES,
  'doNotContact',
  'alreadyDone',
  'updateRecords',
  'userMoved',
  'userMovedNeedCity',
  'changedVehicle',
  'boughtCar',
  'boughtCarNeedModel',
  'doNotNotify',
];

const MAX_FIRESTORE_IN_VALUES = 30;

@Component({
  selector: 'app-hl-conversation-list',
  templateUrl: './hl-conversation-list.component.html',
  styleUrls: ['./hl-conversation-list.component.scss'],
})
export class HlConversationListComponent extends ComponentWithDestroy implements OnInit, OnDestroy {
  @Output()
  scrollEmitter: EventEmitter<any>;

  @Output()
  conversationClickEmitter: EventEmitter<any>;

  @Output()
  refreshConversationsEmitter: EventEmitter<any>;

  @Input() selectedConversationId: string;
  @Input() bot: BotModel;
  @Input() preloadedConversation: HLConversationModel;
  @Input() isFordDealer: boolean = false;

  loading = true;
  previousConversationDataLength = 0;
  conversationData: HLConversationModel[] = [];
  conversationSearchResult: HLConversationModel[] = [];
  currentUserId: string;
  searchText = '';
  searchMode = false;

  clicked = false;

  touchedFilter = false;
  assignedToMeFilter = false;
  highScoreFilter: boolean = false;
  mediumScoreFilter: boolean = false;
  setReadFlag = false;

  // lastScroll: moment.Moment;

  public intervals: TimeInterval[] = [
    new TimeInterval('1d'),
    new TimeInterval('7d'),
    new TimeInterval('30d'),
    new TimeInterval('MTD'),
    new TimeInterval('YTD'),
    new TimeInterval('MAX'),
  ];

  public intentList: IntentFilterOption[] = [];
  public isIntentDropdownOpen = false;
  public intentSearchText = '';
  public showSelectedIntentOnly = false;
  public intentDraftSelectedValues: string[] = [];
  public selectedIntents: string[] = [];

  private localStorage: StorageService;

  private _selectedInterval = new TimeInterval('MAX');

  public set selectedInterval(interval: TimeInterval) {
    this._selectedInterval = interval;
    this.filterConversations();
  }

  public get selectedInterval(): TimeInterval {
    return this._selectedInterval;
  }

  public get selectedIntentLabel(): string {
    if (this.selectedIntents.length === 0) {
      return 'No Intents Selected';
    }

    if (this.allIntentsSelected) {
      return 'All Intents Selected';
    }

    const selectedGroupLabels = this.groupedIntentList
      .filter(group => this.isIntentGroupSelectedBy(group.values, this.selectedIntents))
      .map(group => group.label);

    if (selectedGroupLabels.length === 1) {
      return selectedGroupLabels[0] ?? '1 Intent Selected';
    }

    return `${selectedGroupLabels.length} Intents Selected`;
  }

  public get groupedIntentList(): IntentFilterGroupOption[] {
    const groupedByLabel = new Map<string, string[]>();

    this.availableIntentList.forEach(intent => {
      const currentValues = groupedByLabel.get(intent.label) ?? [];
      if (!currentValues.includes(intent.value)) {
        groupedByLabel.set(intent.label, [...currentValues, intent.value]);
      }
    });

    return Array.from(groupedByLabel.entries())
      .map(([label, values]) => ({ label, values }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  private get availableIntentList(): IntentFilterOption[] {
    return this.intentList.filter(intent => AVAILABLE_INTENT_CODES.includes(intent.value));
  }

  private get allIntentsSelected(): boolean {
    const allIntentValues = this.availableIntentList.map(intent => intent.value);
    return allIntentValues.length > 0 && allIntentValues.every(intentValue => this.selectedIntents.includes(intentValue));
  }

  public get filteredIntentList(): IntentFilterGroupOption[] {
    const query = this.intentSearchText.trim().toLowerCase();
    let filteredIntents = this.groupedIntentList;
    if (this.showSelectedIntentOnly) {
      filteredIntents = filteredIntents.filter(intentGroup => this.isIntentGroupFullySelected(intentGroup.values));
    }
    if (!query) {
      return filteredIntents;
    }
    return filteredIntents.filter(
      intentGroup => intentGroup.label.toLowerCase().includes(query) || intentGroup.values.some(intentCode => intentCode.toLowerCase().includes(query)),
    );
  }

  public get hasFilteredIntents(): boolean {
    return this.filteredIntentList.length > 0;
  }

  public get allFilteredIntentsSelected(): boolean {
    return this.filteredIntentList.length > 0 && this.filteredIntentList.every(intentGroup => this.isIntentGroupFullySelected(intentGroup.values));
  }

  private userColors = [
    '#780078',
    '#787800',
    '#007878',
    '#FF7800',
    '#00FF78',
    '#7800FF',
    '#0078FF',
    '#78FF78',
    '#7878FF',
    '#FF7878',
    '#FF00FF',
    '#FFFF00',
    '#00FFFF',
    '#78FF00',
    '#0078FF',
    '#FF0078',
    '#00FF78',
    '#FF78FF',
    '#FFFF78',
    '#78FFFF',
  ];

  private userList: object[] = [];

  constructor(
    public humanInLoopService: HumanInLoopService,
    private authService: AuthService,
    private apiGatewayAnalyticsService: ApiGatewayAnalyticsService,
    private storageFactory: StorageServiceFactory,
  ) {
    super();
    this.scrollEmitter = new EventEmitter<any>();
    this.conversationClickEmitter = new EventEmitter<any>();
    this.refreshConversationsEmitter = new EventEmitter<any>();
    this.localStorage = this.storageFactory.createLocalStorage();
  }

  private get customDefaultStorageKey(): string {
    return `hil.intent-filter.${this.currentUserId}.custom-default`;
  }

  ngOnInit(): void {
    from(this.loadInitialData())
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initializeSubscriptions());
  }

  private async loadInitialData(): Promise<void> {
    let currentUser: any;
    if (!this.currentUserId) {
      try {
        currentUser = await this.authService.currentUser;

        if (!currentUser) {
          throw new Error('No user profile found');
        }
        this.currentUserId = currentUser.uid;
      } catch (error) {
        console.error('Error in HL Messages List', error);
      }
    }
    await this.loadIntentList();
  }

  private initializeSubscriptions(): void {
    this.filterConversations();
    this.humanInLoopService.clearData('conversations');
    this.humanInLoopService.conversationData.pipe(takeUntil(this.destroy$)).subscribe((data: HLConversationModel[]) => {
      if (this.preloadedConversation) {
        const filtered = data.filter(c => c?.firebaseDocumentId !== this.preloadedConversation?.firebaseDocumentId);
        if (filtered.length === data.length) {
          data = [this.preloadedConversation, ...filtered];
        }
      }

      if (this.searchMode) {
        const conversationsById = new Map(data.map(conversation => [conversation.firebaseDocumentId, conversation]));
        this.conversationData = this.conversationData.map(conversation => conversationsById.get(conversation.firebaseDocumentId) ?? conversation);
      }

      if (!this.clicked && (!this.searchMode || !this.conversationData.length)) {
        this.conversationData = this.filterAndReturnData(data);
        // if data was previously empty but now has data
        // select the conversation to display
        if (this.previousConversationDataLength === 0) {
          this.selectFirstConversation();
        }

        this.previousConversationDataLength = data.length;
      }
      this.loading = this.conversationData.length == 0 && this.loading;
    });
    this.humanInLoopService.conversationData
      .pipe(
        map(data => data.find(conversation => conversation.firebaseDocumentId === this.selectedConversationId)),
        distinctUntilChanged(isEqual),
        takeUntil(this.destroy$),
      )
      .subscribe(selectedConversation => {
        if (selectedConversation) {
          this.conversationClickEmitter.emit({ conversation: selectedConversation, isAutoSelectedConversation: false });
        }
      });
  }
  public timestampToDate(lastMessageDate, lastUpdated): Date {
    if (lastMessageDate === undefined) {
      return lastUpdated.toDate();
    }
    return lastMessageDate.toDate();
  }

  handleScroll(e): void {
    this.scrollEmitter.emit(e);
  }

  scrollToConversation(conversationId: string) {
    const conversationDiv = document.getElementById(`conversation-item-${conversationId}`);
    if (conversationDiv) {
      const allConversationsDiv = document.getElementById('all-conversations-list');
      if (allConversationsDiv) {
        allConversationsDiv.scrollTop = conversationDiv.offsetTop - allConversationsDiv.offsetTop;
      }
    }
  }

  saveSessionParameters() {
    try {
      const storage = window.localStorage;
      if (storage) {
        const value = storage.getItem('dashboardParameters') || null;
        if (value !== null) {
          const json = JSON.parse(value);
          if (json && json.conversationId) {
            delete json.conversationId;
            storage.setItem('dashboardParameters', JSON.stringify(json));
          }
        }
      }
    } catch (err) {
      console.log('Failed storing messages', err);
    }
  }
  async handleConversationClick(conversation: HLConversationModel) {
    if (conversation.firebaseDocumentId && this.setReadFlag) {
      const timeout = this.preloadedConversation ? 2000 : 500;
      const conversationId: string = conversation.firebaseDocumentId;

      if (conversation.status !== HLConversationStatus.Read) {
        // Check if current user is whitelisted from triggering HIL status changes
        const isWhitelisted = await this.humanInLoopService.isWhitelistedFromHILStatusChange();

        // Only update status if user is not whitelisted
        if (!isWhitelisted) {
          this.clicked = true;
          setTimeout(() => {
            let corpId = conversation.botId && conversation.botId.indexOf('-') > 1 ? conversation.botId.split('-')[0] : undefined;
            corpId = corpId || conversation['corporation'];
            corpId = corpId || '';

            this.humanInLoopService.updateConversationReadStatus(corpId, conversationId, true).then(() => {
              conversation.status = HLConversationStatus.Read;
              this.clicked = false;
            });
          }, timeout);
        }
      }
    }
    const data: IConversationClickEvent = {
      conversation,
      isAutoSelectedConversation: false,
    };
    this.setReadFlag = true;
    if (this.searchMode) {
      if (this.preloadedConversation) {
        this.preloadedConversation = conversation;
      }
    }
    this.conversationClickEmitter.emit(data);
  }

  componentToHex(c) {
    const hex = c.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }
  rgbToHex(r, g, b) {
    return '#' + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
  }

  getAvatarColor(agent: HLConversationAgent) {
    if (agent.agentAvatarUrl) {
      return agent.agentAvatarUrl;
    }
    for (const user of this.userList) {
      const key = 'agentId';
      if (agent.agentId === user[key]) {
        const colorKey = 'color';
        return user[colorKey];
      }
    }
    const Red = Math.floor(Math.random() * 210) + 10;
    const Green = Math.floor(Math.random() * 220) + 20;
    const Blue = Math.floor(Math.random() * 200) + 30;

    const userObj = {
      agentId: agent.agentId,
      color: this.userColors.length > 0 ? this.userColors[0] : this.rgbToHex(Red, Green, Blue),
    };
    this.userList.push(userObj);
    if (this.userColors.length) {
      this.userColors.splice(0, 1);
    }
    return userObj.color;
  }
  refreshConversations(filters: IFirebaseFilterCriteria[]): void {
    this.refreshConversationsEmitter.emit(filters);
  }

  private isNeedleInHaystack(needle: string) {
    return haystack => {
      return haystack && haystack.toLowerCase().includes(needle.toLowerCase());
    };
  }

  doSearchConversationsList(query: string) {
    if (query.trim().length > 0) {
      const isQueryPresent = this.isNeedleInHaystack(query);

      // search user data
      this.conversationSearchResult = this.conversationData.filter(convo => {
        if (convo.userData) {
          const { fullName } = convo.userData;
          const { toEmailAddresses } = convo;
          return (
            isQueryPresent(fullName) ||
            (toEmailAddresses && toEmailAddresses.length && isQueryPresent(toEmailAddresses[0])) ||
            (toEmailAddresses && toEmailAddresses.length > 1 && isQueryPresent(toEmailAddresses[1]))
          );
        }
        return false;
      });
      if (this.conversationSearchResult.length === 0) {
        // search with channel equals
        this.conversationSearchResult = this.conversationData.filter(c => {
          return c.lastChannel.trim().toLowerCase() === query.trim().toLowerCase();
        });
      }
      if (this.conversationSearchResult.length === 0) {
        // search with id equals
        this.conversationSearchResult = this.conversationData.filter(c => {
          return c.firebaseDocumentId && c.firebaseDocumentId.trim().toLowerCase() === query.trim().toLowerCase();
        });
      }

      if (this.conversationSearchResult.length === 0) {
        // search with id contains
        this.conversationSearchResult = this.conversationData.filter(c => {
          return c.firebaseDocumentId && c.firebaseDocumentId.trim().toLowerCase().includes(query.trim().toLowerCase());
        });
      }
      if (this.conversationSearchResult.length === 0) {
        // search with tag equals
        this.conversationSearchResult = this.conversationData.filter(c => {
          return c.tags && c.tags.some(tag => tag.trim().toLowerCase() === query.trim().toLowerCase());
        });
      }
      if (this.conversationSearchResult.length > 0) {
        this.conversationData = this.conversationSearchResult;
      }
      this.searchMode = true;
      return;
    }
    this.searchMode = false;
    return;
  }

  getUsersName(conversation: HLConversationModel) {
    if (conversation.userData && conversation.userData.fullName && conversation.userData.fullName.indexOf('null') < 0) {
      return conversation.userData.fullName;
    }
    if (conversation.userData?.firstName && conversation.userData?.lastName) {
      return conversation.userData.fullName + ' ' + conversation.userData?.lastName;
    }
    if (conversation.userData?.firstName) {
      return conversation.userData.fullName;
    }
    if (conversation.userData?.lastName) {
      return conversation.userData?.lastName;
    }
    if (conversation.userData?.email) {
      return conversation.userData?.email;
    }
    if (conversation.userData?.phoneNumber) {
      return conversation.userData?.phoneNumber;
    }
    return 'Guest';
  }

  getAgentInitials(name: string) {
    const names = name.split(' ');
    if (names.length <= 1) {
      return name.substring(0, 1).toUpperCase();
    }
    return names[0].substring(0, 1).toUpperCase() + names[1].substring(0, 1).toUpperCase();
  }

  private filterAndReturnData(data: HLConversationModel[]): HLConversationModel[] {
    let filteredData = data;
    if (this.touchedFilter) {
      filteredData = filteredData.filter(item => item.touched === false);
    }
    if (this.selectedInterval && this.selectedInterval.getType() !== 'MAX') {
      filteredData = filteredData.filter(
        // tslint:disable-next-line: no-non-null-assertion
        item => item.lastMessageDate >= firestore.Timestamp.fromDate(this.selectedInterval.getPastDate()!),
      );
    }

    if (this.selectedIntents.length === 0) {
      filteredData = [];
    } else if (!this.allIntentsSelected) {
      filteredData = filteredData.filter(item => item.intent != null && this.selectedIntents.includes(item.intent));
    }

    return filteredData;
  }

  onIntentDropdownShown(): void {
    this.intentDraftSelectedValues = [...this.selectedIntents];
    this.intentSearchText = '';
    this.showSelectedIntentOnly = false;
  }

  toggleIntentDraftFilter(intentValues: string[]): void {
    if (this.isIntentGroupFullySelected(intentValues)) {
      this.intentDraftSelectedValues = this.intentDraftSelectedValues.filter(selectedIntent => !intentValues.includes(selectedIntent));
      return;
    }
    this.intentDraftSelectedValues = Array.from(new Set([...this.intentDraftSelectedValues, ...intentValues]));
  }

  onIntentOptionClick(event: MouseEvent, intentValues: string[]): void {
    event.stopPropagation();
    this.toggleIntentDraftFilter(intentValues);
  }

  toggleAllFilteredIntents(checked: boolean): void {
    const filteredIntentValues = this.filteredIntentList.flatMap(intentGroup => intentGroup.values);
    if (checked) {
      this.intentDraftSelectedValues = Array.from(new Set([...this.intentDraftSelectedValues, ...filteredIntentValues]));
      return;
    }
    this.intentDraftSelectedValues = this.intentDraftSelectedValues.filter(selectedIntent => !filteredIntentValues.includes(selectedIntent));
  }

  applyIntentFilter(): void {
    this.selectedIntents = [...this.intentDraftSelectedValues];
    this.filterConversations();
    this.isIntentDropdownOpen = false;
  }

  private async loadIntentList(): Promise<void> {
    try {
      const intents = await this.apiGatewayAnalyticsService.getIntents();
      const mappedIntentList = this.mapIntentFilters(this.consolidateIntentDescriptions(intents));

      if (mappedIntentList.length > 0) {
        this.intentList = [ASSISTANT_STOPPED_INTENT, ...mappedIntentList];
        const validIntentValues = new Set(this.intentList.map(intent => intent.value));
        this.selectedIntents = this.loadInitialIntentSelection(validIntentValues);
        this.intentDraftSelectedValues = [...this.selectedIntents];
      }
    } catch {
      // keep latest loaded/empty state
    }
  }

  private loadInitialIntentSelection(validIntentValues: Set<string>): string[] {
    const saved = this.localStorage.get(this.customDefaultStorageKey);
    if (saved) {
      try {
        const parsed: string[] = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          const validSavedIntents = parsed.filter(intent => validIntentValues.has(intent));
          if (parsed.length === 0 || validSavedIntents.length > 0) {
            return validSavedIntents;
          }
        }
      } catch {
        // fall through to original default
      }
    }
    return DEFAULT_SELECTED_INTENT_CODES.filter(intent => validIntentValues.has(intent));
  }

  saveAsCustomDefault(): void {
    this.localStorage.set(this.customDefaultStorageKey, JSON.stringify(this.intentDraftSelectedValues));
    this.applyIntentFilter();
  }

  restoreOriginalDefault(): void {
    this.localStorage.remove(this.customDefaultStorageKey);
    const validIntentValues = new Set(this.intentList.map(intent => intent.value));
    this.intentDraftSelectedValues = DEFAULT_SELECTED_INTENT_CODES.filter(intent => validIntentValues.has(intent));
    this.applyIntentFilter();
  }

  private mapIntentFilters(intents: Intent[]): IntentFilterOption[] {
    return intents
      .map(intent => {
        const value = (intent.intent_code || intent.intent_description || '').trim();
        const label = (intent.intent_description || intent.intent_code || '').trim();
        return { label, value };
      })
      .filter(intent => intent.label.length > 0 && intent.value.length > 0);
  }

  private consolidateIntentDescriptions(intents: Intent[]): Intent[] {
    return intents.map(intent => ({
      ...intent,
      intent_description: intent.consolidate_intent
        ? intents.find(parentIntent => parentIntent.intent_id === intent.consolidate_intent)?.intent_description ?? intent.intent_description
        : intent.intent_description,
    }));
  }

  isIntentGroupSelected(intentValues: string[]): boolean {
    return this.isIntentGroupFullySelected(intentValues);
  }

  private isIntentGroupFullySelected(intentValues: string[]): boolean {
    return intentValues.every(intentValue => this.intentDraftSelectedValues.includes(intentValue));
  }

  private isIntentGroupSelectedBy(intentValues: string[], selectedValues: string[]): boolean {
    return intentValues.some(intentValue => selectedValues.includes(intentValue));
  }

  filterConversations() {
    const filters: IFirebaseFilterCriteria[] = [];
    if (this.touchedFilter) {
      filters.push({
        field: 'touched',
        operator: '==',
        value: !this.touchedFilter,
      });
    }
    if (this.assignedToMeFilter) {
      filters.push({
        field: 'assignmentUserIds',
        operator: 'array-contains',
        value: this.currentUserId,
      });
    }

    if (!this.allIntentsSelected) {
      if (this.selectedIntents.length === 1) {
        filters.push({
          field: 'intent',
          operator: '==',
          value: this.selectedIntents[0],
        });
      }

      if (this.selectedIntents.length > 1 && this.selectedIntents.length <= MAX_FIRESTORE_IN_VALUES) {
        filters.push({
          field: 'intent',
          operator: 'in',
          value: this.selectedIntents,
        });
      }
    }

    let lastUpdated: firestore.Timestamp = firestore.Timestamp.fromDate(new Date(2000, 1, 1));
    if (this.selectedInterval && this.selectedInterval.getType() !== 'MAX') {
      // tslint:disable-next-line: no-non-null-assertion
      lastUpdated = firestore.Timestamp.fromDate(this.selectedInterval.getPastDate()!);
    }
    filters.push({
      field: 'lastUpdated',
      operator: '>',
      value: lastUpdated,
    });

    if (this.highScoreFilter && this.mediumScoreFilter) {
      filters.push({
        field: 'esmScore',
        operator: 'in',
        value: [3, 4, 5, 6, 7, 8, 9, 10],
      });
    } else {
      if (this.highScoreFilter) {
        filters.push({
          field: 'esmScore',
          operator: 'in',
          value: [7, 8, 9, 10],
        });
      }

      if (this.mediumScoreFilter) {
        filters.push({
          field: 'esmScore',
          operator: 'in',
          value: [3, 4, 5, 6],
        });
      }
    }

    this.refreshConversations(filters);
  }

  clearSearchFilter() {
    this.searchText = '';
    this.filterConversations();
    this.searchMode = false;
    this.previousConversationDataLength = 0;
  }

  onSearchTextChange() {
    // Naive clear mechanism
    if (this.searchText.length === 0) {
      this.searchMode = false;
      this.filterConversations();
    }
  }

  private selectFirstConversation(): void {
    if (this.preloadedConversation && this.preloadedConversation.firebaseDocumentId) {
      // select preloaded convo and scroll to it
      this.setReadFlag = true;
      this.handleConversationClick(this.preloadedConversation);
      const conversationId = this.preloadedConversation.firebaseDocumentId;
      setTimeout(() => {
        this.scrollToConversation(conversationId);
      }, 100);
    } else {
      if (this.conversationData.length > 0) {
        // select first conversation on load
        this.setReadFlag = false;
        this.handleConversationClick(this.conversationData[0]);
      }
    }
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.saveSessionParameters();
  }

  public getExtraTooltip(assignments: any[]): string {
    return assignments
      .slice(2)
      .map(a => a.agentName)
      .join(', ');
  }
}
